import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Player } from "@mdj/shared";
import { api } from "../lib/api";
import { WalkupPlayer, type Phase } from "../lib/playback";
import { Button, GlassCard } from "../components/Glass";

type Cache = { announcement: string | null; clip: string | null };

// Game day: tap a batter -> announcement, then their walk-up clip.
export function GameDay() {
  const [players, setPlayers] = useState<Player[] | null>(null);
  const [cache, setCache] = useState<Record<string, Cache>>({});
  const [phase, setPhase] = useState<Phase>("idle");
  const [current, setCurrent] = useState<Player | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const player = useMemo(() => new WalkupPlayer(setPhase), []);
  const urls = useRef<string[]>([]);

  // Load the roster, then prefetch every batter's audio so a tap plays at once.
  useEffect(() => {
    let live = true;
    api.listPlayers().then(async (ps) => {
      if (!live) return;
      setPlayers(ps);
      for (const p of ps) {
        const entry: Cache = { announcement: null, clip: null };
        try { if (p.announcementKey) entry.announcement = await api.announcementUrl(p.id); } catch { /* none */ }
        try { if (p.walkupClipId) entry.clip = await api.clipAudioUrl({ id: p.walkupClipId }); } catch { /* none */ }
        if (!live) return;
        urls.current.push(...[entry.announcement, entry.clip].filter((u): u is string => !!u));
        setCache((c) => ({ ...c, [p.id]: entry }));
      }
    }).catch((e: Error) => setErr(e.message));
    return () => { live = false; player.stop(); urls.current.forEach((u) => u.startsWith("blob:") && URL.revokeObjectURL(u)); };
  }, [player]);

  const tap = (p: Player) => {
    const c = cache[p.id];
    if (!c) return;
    if (current?.id === p.id && phase !== "idle") { player.stop(); setCurrent(null); return; }
    setCurrent(p);
    player.play(c.announcement, c.clip);
  };

  const ready = (p: Player) => Boolean(cache[p.id]);
  const has = (p: Player) => Boolean(p.announcementKey || p.walkupClipId);

  return (
    <GlassCard key="gameday">
      <h2>Game day</h2>
      <p className="hint">Tap the batter. The announcement plays, then their song.</p>

      <AnimatePresence>
        {current && phase !== "idle" && (
          <motion.div key="now" className="now glass glass-strong" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}>
            <div className="now-num">{current.number ?? "•"}</div>
            <div className="now-body">
              <div className="now-label">{phase === "announcing" ? "Now batting…" : "Walk-up"}</div>
              <div className="now-name">{current.name}</div>
              <div className="bars" aria-hidden>
                {Array.from({ length: 12 }).map((_, i) => (
                  <motion.i key={i} animate={{ height: [6, 10 + ((i * 7) % 24), 6] }} transition={{ duration: 0.5 + (i % 4) * 0.1, repeat: Infinity, ease: "easeInOut" }} />
                ))}
              </div>
            </div>
            <Button icon onClick={() => { player.stop(); setCurrent(null); }} aria-label="Stop" style={{ width: 56, height: 56, fontSize: 20 }}>■</Button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="players" role="list">
        {players === null && <p className="hint">Loading roster…</p>}
        {players?.length === 0 && <p className="hint">No players yet — set one up first.</p>}
        {players?.map((p) => (
          <motion.button
            key={p.id} type="button" className={`player${current?.id === p.id && phase !== "idle" ? " on" : ""}`}
            onClick={() => tap(p)} disabled={!has(p) || !ready(p)} whileTap={{ scale: 0.98 }}
            aria-label={`Play ${p.name}`}
          >
            <span className="num">{p.number ?? "•"}</span>
            <span className="name">{p.name}</span>
            <span className="meta">
              {!has(p) ? "not set up" : !ready(p) ? "loading…" : `${p.announcementKey ? "📣 " : ""}${p.walkupClipId ? "🎵" : ""}`}
            </span>
          </motion.button>
        ))}
      </div>
      {err && <p style={{ margin: 0, color: "var(--danger)", fontSize: 13 }}>{err}</p>}
    </GlassCard>
  );
}
